import { useEffect, useState } from 'react';
import { useTranslation } from '../../../lib/i18n';
import { useNarrow } from '../../../lib/use-narrow';
import { useLoadBi } from '../../../lib/use-load-bi';
import { downloadArtifact, openArtifact } from '../../../lib/artifact-file';
import { DocTooLargeError } from '../../../lib/doc-content';
import { useDocBlob } from '../../../lib/doc-bytes';
import { printPdfObjectUrl, registerDocumentPrinter } from '../../../lib/doc-print';
import { docSource } from '../../../lib/doc-viewer';
import type { WorkspaceFile } from '../../../lib/workspace-files';
import { effectiveContent } from '../../../lib/file-record';
import { Button, Typography } from '../ui';
import { DocErrorState, DocShimmer, HandOffState } from './DocViewerStates';
import styles from './DocViewerModal.module.css';

// A PDF, rendered by the browser's own viewer over bytes WE fetched.
//
// WHY NOT point the embed at the signed URL directly: a WixMP response carries whatever
// `Content-Disposition` WixMP chose, and we do not control it — so `<embed src={signedUrl}>` may make the
// browser download the file instead of rendering it, which is the behaviour this whole feature replaces. A
// `blob:` URL has no such header and carries the `Content-Type` we stamp in `fetchDocBlob`, so the preview is
// deterministic. It also keeps the signed URL out of the DOM.
//
// `pdf` is NOT one of `DocViewerBody`'s text kinds: it needs bytes, so it resolves its own source and owns its
// own loading / too-large / error states. Those come from the SHARED `DocViewerStates`, so this view cannot
// grow a second flavour of the same three states — `test/doc-viewer-wiring.test.ts` asserts it renders all of
// them, i.e. that it never renders nothing.
export function PdfDocView({ file, onBi }: { file: WorkspaceFile; onBi: (action: string) => void }) {
  const { t } = useTranslation();
  const narrow = useNarrow();
  const source = docSource(effectiveContent(file));
  const url = source?.kind === 'remote' ? source.url : null;

  const query = useDocBlob(url);
  useLoadBi('doc-viewer-pdf', { isLoading: query.isLoading, isError: query.isError }, { enabled: !!url });

  const objectUrl = useObjectUrl(query.data ?? null);

  // The parent print job cannot paginate the `<embed>`'s document, so this view prints the PDF itself — see
  // `printPdfObjectUrl`. Registered only while the bytes are actually on screen; without them Print falls
  // back to `window.print()`, which prints whichever hand-off state is showing (and says why it is showing).
  useEffect(() => {
    if (!objectUrl) return;
    return registerDocumentPrinter(() => printPdfObjectUrl(objectUrl));
  }, [objectUrl]);

  const download = () => {
    onBi('download');
    void downloadArtifact(file);
  };

  // A PDF has no inline form on web — no source means there is nothing to fetch, which is permanent, so it
  // gets the no-retry hand-off rather than the error state.
  if (!url) return <HandOffState message={t('docViewer.pdfUnavailable')} onDownload={download} />;

  // iOS Safari — and therefore EVERY browser on iOS, which are all WebKit — renders only the FIRST PAGE of an
  // embedded PDF, with no way to scroll to the rest. A one-page-only viewer that gives no hint the document
  // continues is worse than not offering one, so a phone gets a poster that hands off instead. This is the
  // easiest thing in this component to "simplify" away; don't.
  if (narrow) {
    return (
      <div className={styles.state}>
        <Typography variant="p2" color="secondary" component="p">
          {t('docViewer.pdfMobile')}
        </Typography>
        <div className={styles.pdfPosterActions}>
          <Button
            onClick={() => {
              onBi('open-new-tab');
              openArtifact(file);
            }}
          >
            {t('docViewer.openInNewTab')}
          </Button>
          <Button variant="outlined" onClick={download}>
            {t('packs.download')}
          </Button>
        </div>
      </div>
    );
  }

  // `pdfViewerEnabled` is `false` on a browser that will not render a PDF inline (and `undefined` where the
  // property is unimplemented). `<embed>` is a void element with no fallback-content mechanism, so a probe is
  // the only way to offer a hand-off rather than a blank box. `undefined` renders optimistically: a browser
  // that cannot cope shows its own placeholder, which beats hiding a working preview on a false negative.
  if (typeof navigator !== 'undefined' && navigator.pdfViewerEnabled === false) {
    return <HandOffState message={t('docViewer.pdfUnsupported')} onDownload={download} />;
  }

  if (query.error instanceof DocTooLargeError) return <HandOffState message={t('docViewer.tooLarge')} onDownload={download} />;

  if (query.isError) {
    return (
      <DocErrorState
        onRetry={() => {
          onBi('retry');
          void query.refetch();
        }}
      />
    );
  }

  // Also covers the gap between the bytes arriving and the effect minting the URL — pointing the embed at a
  // null/stale `src` for one paint is what makes Chrome flash a broken-plugin box.
  if (!objectUrl) return <DocShimmer />;

  return (
    <embed
      // Re-keying on the URL forces a fresh element per blob. On a Retry, React runs the previous effect's
      // cleanup (which revokes) BEFORE the next effect mints a replacement, so a reused element would spend a
      // paint pointing at a revoked URL.
      key={objectUrl}
      className={styles.pdfEmbed}
      src={objectUrl}
      type="application/pdf"
      aria-label={file.name}
    />
  );
}

/** An object URL for `blob`, revoked when the blob changes or the view unmounts.
 *
 *  Revoked on UNMOUNT, not on a timer. `artifact-file.ts` uses `setTimeout(revoke, 60_000)` and is right to:
 *  it hands the URL to a tab it does not control and cannot know when that tab is done. Here the `<embed>`
 *  lives exactly as long as this component, so a timer would instead leak a multi-megabyte blob for a minute
 *  on every open. React Query keeps the *Blob* cached, so re-opening the same PDF re-mints instantly with no
 *  refetch. */
function useObjectUrl(blob: Blob | null): string | null {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!blob) {
      setObjectUrl(null);
      return;
    }
    const next = URL.createObjectURL(blob);
    setObjectUrl(next);
    return () => {
      URL.revokeObjectURL(next);
      setObjectUrl(null);
    };
  }, [blob]);
  return objectUrl;
}
